import {Scene} from '@babylonjs/core/scene.js';
import {Mesh} from '@babylonjs/core/Meshes/mesh.js';
import {VertexData} from '@babylonjs/core/Meshes/mesh.vertexData.js';
import {AbstractMesh} from '@babylonjs/core/Meshes/abstractMesh.js';
import {TransformNode} from '@babylonjs/core/Meshes/transformNode.js';
import {StandardMaterial} from '@babylonjs/core/Materials/standardMaterial.js';
import {Color3} from '@babylonjs/core/Maths/math.color.js';
import {Vector3} from '@babylonjs/core/Maths/math.vector.js';
import {MeshBuilder} from '@babylonjs/core/Meshes/meshBuilder.js';
import {Material} from '@babylonjs/core/Materials/material.js';
import {Assets} from './assets';
import {CONFIG,cell,height,treePositions} from './world';
export function terrain(scene:Scene,name:string,x:number,z:number,size:number,mat:Material,offset=0,road=false){
 const mesh=new Mesh(name,scene),positions:number[]=[],indices:number[]=[],uvs:number[]=[];
 const n=size/10;
 for(let j=0;j<=n;j++)for(let i=0;i<=n;i++){const px=x+i*10,pz=z+j*10;positions.push(px,height(px,pz)+offset,pz);uvs.push(px/5,pz/5);}
 for(let j=0;j<n;j++)for(let i=0;i<n;i++){
  const px=x+i*10,pz=z+j*10;
  if(road&&!((Math.abs(px+5)<10&&Math.abs(pz+5)<10)||(px===0&&pz>=-30&&pz<30)||(pz===0&&px>=-30&&px<30)||(pz===-30&&px>=-20&&px<10)||(pz===-20&&px>=10&&px<20)))continue;
  const a=j*(n+1)+i,b=a+1,c=a+n+1,d=c+1;indices.push(a,b,c,b,d,c);
 }
 if(indices.length===0){mesh.dispose();return null;}
 const normals:number[]=[];VertexData.ComputeNormals(positions,indices,normals);
 const data=new VertexData();data.positions=positions;data.indices=indices;data.normals=normals;data.uvs=uvs;data.applyToMesh(mesh);
 mesh.sideOrientation=Material.CounterClockWiseSideOrientation;
 mesh.metadata={terrain:!road,chunkX:x,chunkZ:z};
 mesh.material=mat;mesh.receiveShadows=true;mesh.isPickable=false;return mesh;
}
type Chunk={overlay?:AbstractMesh;meshes:AbstractMesh[];roots:TransformNode[];trees:AbstractMesh[]};
export class ChunkManager{
 loaded=new Map<string,Chunk>();pending=new Set<string>();errors:string[]=[];lastMs=0;maxPending=0;
 private wanted=new Set<string>();private queue:{x:number;z:number;key:string;rank:number}[]=[];private current={x:10,z:10};
 debug=false;hideDecor=false;
 private debugMat:StandardMaterial;private currentMat:StandardMaterial;
 constructor(private scene:Scene,private assets:Assets){
  this.debugMat=new StandardMaterial('terrain-debug',scene);this.debugMat.diffuseColor=new Color3(.05,1,.05);this.debugMat.emissiveColor=new Color3(.02,.6,.02);this.debugMat.backFaceCulling=true;
  this.currentMat=this.debugMat.clone('terrain-current');this.currentMat.diffuseColor=new Color3(1,.7,.03);this.currentMat.emissiveColor=new Color3(.7,.3,0);
 }
 setDebug(value:boolean){this.debug=value;this.refreshDebug();}
 setHideDecor(value:boolean){this.hideDecor=value;this.refreshDebug();}
 private refreshDebug(){
  for(const [key,ch]of this.loaded){
   const [cx,cz]=key.split(',').map(Number);
   ch.meshes[0].material=this.debug?(cx===this.current.x&&cz===this.current.z?this.currentMat:this.debugMat):this.assets.surfaceMaterial('elmir_grass_5m');
   ch.overlay?.setEnabled(this.debug);
   for(const m of ch.meshes)if(m.name.startsWith('road-'))m.setEnabled(!this.debug);
   for(const t of ch.trees)t.setEnabled(!this.hideDecor&&Math.max(Math.abs(cx-this.current.x),Math.abs(cz-this.current.z))<=CONFIG.treeRadius);
  }
 }
 update(x:number,z:number,dx=0,dz=0){
  const c=cell(x,z);this.current=c;this.wanted.clear();
  const candidates=new Map<string,{x:number;z:number;key:string;rank:number}>();
  const add=(cx:number,cz:number)=>{if(cx<0||cx>19||cz<0||cz>19)return;const key=cx+','+cz;this.wanted.add(key);candidates.set(key,{x:cx,z:cz,key,rank:Math.hypot(cx-c.x,cz-c.z)});};
  for(let i=-CONFIG.radius;i<=CONFIG.radius;i++)for(let j=-CONFIG.radius;j<=CONFIG.radius;j++)add(c.x+i,c.z+j);
  // One extra leading row before the next cell boundary, independent of speed.
  if(Math.hypot(dx,dz)>.001){const fx=Math.abs(dx)>=Math.abs(dz)?Math.sign(dx):0,fz=fx?0:Math.sign(dz);
   for(let i=-CONFIG.radius;i<=CONFIG.radius;i++)add(c.x+fx*(CONFIG.radius+1)+(fz?i:0),c.z+fz*(CONFIG.radius+1)+(fx?i:0));}
  for(const [key,ch]of this.loaded){
   const [cx,cz]=key.split(',').map(Number);
   if(Math.max(Math.abs(cx-c.x),Math.abs(cz-c.z))>CONFIG.retention){this.dispose(ch);this.loaded.delete(key);}
   else for(const t of ch.trees)t.setEnabled(!this.hideDecor&&Math.max(Math.abs(cx-c.x),Math.abs(cz-c.z))<=CONFIG.treeRadius);
  }
  this.refreshDebug();
  this.queue=[...candidates.values()].filter(t=>!this.loaded.has(t.key)&&!this.pending.has(t.key)).sort((a,b)=>a.rank-b.rank);
 }
 pump(){
  const start=performance.now();let count=0;
  while(this.queue.length&&count<CONFIG.parallel&&performance.now()-start<CONFIG.budgetMs){
   const task=this.queue.shift()!;if(!this.wanted.has(task.key)||this.loaded.has(task.key))continue;
   this.pending.add(task.key);this.maxPending=Math.max(this.maxPending,this.pending.size);const t=performance.now();
   try{const chunk=this.build(task.x,task.z);this.loaded.set(task.key,chunk);this.refreshDebug();this.lastMs=performance.now()-t;}
   catch(e){this.errors.push(task.key+': '+String(e));this.errors=this.errors.slice(-20);console.error(e);}
   finally{this.pending.delete(task.key);}count++;
  }
 }
 private build(cx:number,cz:number):Chunk{
  const meshes:AbstractMesh[]=[],roots:TransformNode[]=[],trees:AbstractMesh[]=[];
  const register=(ms:AbstractMesh[])=>{meshes.push(...ms);for(const m of ms){let r:TransformNode=m;while(r.parent)r=r.parent as TransformNode;if(!roots.includes(r))roots.push(r);}};
  try{
   const x=-500+cx*50,z=-500+cz*50;
   meshes.push(terrain(this.scene,'terrain-'+cx+'-'+cz,x,z,50,this.assets.surfaceMaterial('elmir_grass_5m'))!);
   // Shared stone in village, tinted road material outside (temporary dirt).
   const roadMat=Math.max(Math.abs(x+25),Math.abs(z+25))<=100?this.assets.surfaceMaterial('elmir_road_10m'):this.dirt;
   const roadMesh=terrain(this.scene,'road-'+cx+'-'+cz,x,z,50,roadMat,.035,true);if(roadMesh)meshes.push(roadMesh);
   for(const t of treePositions(cx,cz)){const ms=this.assets.place('elmir_tree',t.x,t.z,height(t.x,t.z));register(ms);trees.push(...ms);}
   const c=this.current;for(const m of trees)m.setEnabled(Math.max(Math.abs(cx-c.x),Math.abs(cz-c.z))<=CONFIG.treeRadius);
   const points:Vector3[]=[];
   for(let i=0;i<=20;i++){let px=x,pz=z;
    if(i<=5){px=x+i*10;}else if(i<=10){px=x+50;pz=z+(i-5)*10;}else if(i<=15){px=x+50-(i-10)*10;pz=z+50;}else{pz=z+50-(i-15)*10;}
    points.push(new Vector3(px,height(px,pz)+.09,pz));
   }
   const overlay=MeshBuilder.CreateLines('chunk-border-'+cx+','+cz,{points},this.scene);overlay.color=new Color3(.02,.1,.02);overlay.setEnabled(this.debug);meshes.push(overlay);
   return {meshes,roots,trees,overlay};
  }catch(e){this.dispose({meshes,roots,trees});throw e;}
 }
 dirt!:Material;
 private dispose(ch:Chunk){for(const r of ch.roots)r.dispose(false,false);for(const m of ch.meshes)if(!m.isDisposed())m.dispose(false,false);}
 snapshot(){return {debug:this.debug,hideDecor:this.hideDecor,visibleTerrain:[...this.loaded.values()].filter(c=>c.meshes[0].isEnabled()&&c.meshes[0].isVisible&&c.meshes[0].getTotalIndices()===150).length,cell:this.current,loaded:this.loaded.size,pending:this.pending.size,queued:this.queue.length,lastMs:this.lastMs,errors:[...this.errors]};}
 disposeAll(){for(const c of this.loaded.values())this.dispose(c);this.loaded.clear();this.queue=[];}
}
